import type { Channel, Partner } from "@expert/shared-types";
import type { ReadonlyDeep } from "type-fest";
import type { AppConfig, AppConfigSingleton, PartnerAppConfig, PartnerConfig, PartnerSearchAppConfig } from "../types";
import { unchannel } from "../utils";

let appConfigSingleton: AppConfigSingleton | undefined;

export const getAppConfigSingleton = () => {
    if (!appConfigSingleton) throw new Error("Tried to get appConfigSingleton when it’s undefined.");
    return appConfigSingleton;
};

function deepFreeze<T>(object: NonNullable<T>): ReadonlyDeep<T> {
    // Freeze properties before freezing self
    for (const key of Reflect.ownKeys(object)) {
        const value: unknown = object[key as keyof typeof object];
        if ((value && typeof value === "object") || typeof value === "function") {
            deepFreeze(value);
        }
    }

    return Object.freeze(object) as ReadonlyDeep<T>;
}

/** Creates and freezes the application configuration singleton */
export const freezeAppConfigSingleton = ({
    appConfig,
    org,
    allowedPartnersOverride,
    channel,
}: {
    appConfig: AppConfig;
    org?: string;
    allowedPartnersOverride?: string;
    channel: Channel;
}) => {
    const partnersConfigByChannel = unchannel(appConfig.partnersConfig, channel) as unknown as PartnerAppConfig;

    appConfigSingleton = {
        amazonConnectInstancesInfo: appConfig.amazonConnectInstancesInfo,
        twilioAppConfig: appConfig.twilioAppConfig,
        getPartnersConfig: setupPartnersConfig(partnersConfigByChannel, org),
        getPartnerSearch: appConfig.partnerSearch ? setupPartnersSearch(appConfig.partnerSearch) : undefined,
        allowedPartners: allowedPartnersOverride
            ? allowedPartnersOverride.split(",")
            : appConfig.allowedPartners.split(","),
        hasPartnerConfig: setupHasPartnerConfig(partnersConfigByChannel, org),
    };
    deepFreeze(appConfigSingleton);
};

/**
 * Curried function that returns partner-specific configuration function
 * @returns A function that returns configuration for a specific partner
 */
const setupPartnersConfig = (config: PartnerAppConfig | undefined, org: string | undefined) => (partner: Partner) => {
    if (!config) return {} as PartnerConfig;
    if (!partner && config.unknown) return config.unknown;
    const partnerWithOrg = org ? `${partner}:${org}` : partner;
    return config[partnerWithOrg] ?? config.unknown;
};

/**
 * Curried function that returns partner-specific search configuration
 * @returns A function that returns search configuration for a specific partner
 */
const setupPartnersSearch = (config: PartnerSearchAppConfig) => (partner: Partner) => {
    return config[partner];
};

/**
 * Curried function that returns a function to check if a partner has a dedicated configuration
 * @returns A function that checks if a partner exists directly in the config
 */
const setupHasPartnerConfig =
    (config: PartnerAppConfig, org: string | undefined) =>
    (partner: Partner): boolean => {
        if (!partner) return false;

        const partnerWithOrg = org ? `${partner}:${org}` : partner;
        // Check if the partner configuration exists directly (not using the fallback to "unknown")
        return Object.hasOwn(config, partnerWithOrg);
    };
